import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { ChevronDown, ChevronUp } from '../../assets';
import { Button, ConfirmDeleteModal, Theme, TopBar } from '../../components';
import { FoodLine, SelectedFoodTrayIndex } from '../../models';
import { foodTrayTemplatesActions, foodTrayTemplatesSelectors } from '../../redux';
import DashboardFooter from './components/DashboardFooter';
import FoodLinePreview from './components/FoodLinePreview';
import LiveGastronom from './components/LiveGastronom';

import './DashBoard.scss';

const DashBoard: FC<RouteComponentProps<{ id: string }>> = ({ history, match }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const templateId = match.params.id;

  const template = useSelector(foodTrayTemplatesSelectors.getFoodTrayTemplate);
  const isReplaceFoodItemLoading = useSelector(foodTrayTemplatesSelectors.isReplaceFoodItemLoading);
  const isReplaceFoodItemAndStartTimerLoading = useSelector(foodTrayTemplatesSelectors.isReplaceFoodItemAndStartTimerLoading);
  const isEmptyItemLoading = useSelector(foodTrayTemplatesSelectors.isEmptyItemLoading);
  const isEmptyLineLoading = useSelector(foodTrayTemplatesSelectors.isEmptyLineLoading);
  const isStartTimerLoading = useSelector(foodTrayTemplatesSelectors.isStartTimerLoading);
  const isStartTimerForExistingFoodItemLoading = useSelector(foodTrayTemplatesSelectors.isStartTimerForExistingFoodItemLoading);

  const [selectedFoodLines, setSelectedFoodLines] = useState<string[] | null>(null);
  const [foodLines, setFoodLines] = useState<FoodLine[] | null>(null);
  const [selectedFoodTray, setSelectedFoodTray] = useState<SelectedFoodTrayIndex>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isConfirmLeaveModalOpen, setIsConfirmLeaveModalOpen] = useState<boolean>(false);
  const [containingFoodLineId, setContainingFoodLineId] = useState<string>('');

  const hasActiveTimers = () =>
    !!(foodLines || []).find(foodLine => {
      return !!foodLine?.foodTrays?.find(foodTray => {
        return !!foodTray?.foodItem?.enteredAt;
      });
    });

  useEffect(() => {
    if (
      !isReplaceFoodItemLoading ||
      !isReplaceFoodItemAndStartTimerLoading ||
      !isEmptyItemLoading ||
      !isEmptyLineLoading ||
      !isStartTimerLoading ||
      !isStartTimerForExistingFoodItemLoading
    ) {
      dispatch(foodTrayTemplatesActions.getFoodTrayTemplate(templateId));
    }
  }, [
    templateId,
    isReplaceFoodItemLoading,
    isReplaceFoodItemAndStartTimerLoading,
    isEmptyItemLoading,
    isEmptyLineLoading,
    isStartTimerLoading,
    isStartTimerForExistingFoodItemLoading,
  ]);

  //TODO: check interval rate
  useEffect(() => {
    window.setInterval(() => {
      dispatch(foodTrayTemplatesActions.getFoodTrayTemplate(templateId));
    }, 30_000);
  }, []);

  useEffect(() => {
    if (template && template.id === templateId) {
      const newFoodLines = template?.foodLines;
      setFoodLines(newFoodLines);
      setSelectedFoodLines(selectedFoodLines || [newFoodLines?.[0].id]);
    }
  }, [template]);

  const setSelection = id => {
    setSelectedFoodTray(null);
    let newSelection = [...selectedFoodLines];
    const index = newSelection.findIndex(s => s === id);
    if (selectedFoodLines.length > 1 && index >= 0) {
      newSelection.splice(index, 1);
    } else if (index === -1) {
      newSelection.push(foodLines.find(f => f.id === id)?.id);
    }
    setSelectedFoodLines(newSelection);
  };

  const handleChangeTemplateBtnClicked = () => {
    if (hasActiveTimers()) {
      setIsConfirmLeaveModalOpen(true);
    } else {
      confirmLeave();
    }
  };

  const confirmLeave = () => {
    setIsConfirmLeaveModalOpen(false);
    history.goBack();
  };

  const renderPreview = (foodLine: FoodLine) => (
    <FoodLinePreview
      key={`preview-${foodLine.id}`}
      isSelected={!!selectedFoodLines.find(s => s === foodLine.id)}
      foodLine={foodLine}
      isCollapsed={isCollapsed}
      setSelection={setSelection}
    />
  );

  const renderFoodLine = (foodLineId: string) => {
    const foodLine = foodLines.find(f => f.id === foodLineId);
    if (!foodLine) {
      return null;
    }
    return (
      <LiveGastronom
        showOrientation
        key={foodLine.id}
        foodTrays={foodLine.foodTrays}
        selectedFoodTray={selectedFoodTray}
        setSelectedFoodTray={setSelectedFoodTray}
        id={foodLine.id}
        containingFoodLineId={containingFoodLineId}
        setContainingFoodLineId={setContainingFoodLineId}
      />
    );
  };

  return (
    <>
      <TopBar>
        <Button theme={Theme.SECONDARY} onClickCallback={handleChangeTemplateBtnClicked} icon='Raster'>
          {t('lblChangeTemplate')}
        </Button>
        <div className='divider' />
      </TopBar>
      <div className='dashboard'>
        <div className='top-food-lines'>
          <Button
            theme={Theme.SECONDARY}
            className='btn-collapse'
            onClickCallback={() => setIsCollapsed(isCollapsed => !isCollapsed)}>
            {isCollapsed ? <ChevronDown /> : <ChevronUp />}
            {t(isCollapsed ? 'lblExpand' : 'lblCollapse')}
          </Button>
          <PerfectScrollbar className='scroll' options={{ suppressScrollY: true, useBothWheelAxes: true }}>
            {foodLines?.map(renderPreview)}
          </PerfectScrollbar>
        </div>
        <div
          className={`active-gastronoms${isCollapsed ? ' collapsed' : ''}${selectedFoodLines?.length === 1 ? ' single-food-line' : ''
            }${!!selectedFoodTray ? ' food-tray-selected' : ''}`}
          onClick={() => setSelectedFoodTray(null)}>
          {selectedFoodLines?.map(renderFoodLine)}
        </div>
        {selectedFoodTray && (
          <DashboardFooter
            selectedFoodTray={selectedFoodTray}
            containingFoodLineId={containingFoodLineId}
            foodLines={foodLines}
          />
        )}
      </div>
      <ConfirmDeleteModal
        isOpen={isConfirmLeaveModalOpen}
        title='titleActiveFoodLine'
        description='lblActiveFoodLineDescription'
        closeModal={() => setIsConfirmLeaveModalOpen(false)}
        onDeleteConfirmed={confirmLeave}
        cancelLabel='lblCancel'
        confirmLabel='lblLeave'
        confirmButtonTheme={Theme.SECONDARY}
      />
    </>
  );
};

export default DashBoard;
